import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { MailCheck } from "lucide-react";
import { api } from "@teamblob/api-client";
import { Button } from "@teamblob/ui/components";

/**
 * `/verify-email-sent` — shown right after registration.
 *
 * Registration creates the account but establishes NO session: access is gated
 * on email verification. This screen tells the user to check their inbox and
 * lets them resend the link. They click the link in the email → the API marks
 * them verified and redirects to /login?verified=1 → they sign in → /welcome.
 */
const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/verify-email-sent")({
  validateSearch: searchSchema,
  component: VerifyEmailSentPage,
});

function VerifyEmailSentPage() {
  const { email } = Route.useSearch();
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );

  const resend = async () => {
    if (email === undefined || email === "") return;
    setStatus("sending");
    try {
      await api.post("/api/v1/auth/verify-email/resend", { email });
      setStatus("sent");
    } catch {
      // Anti-enumeration: the API always 200s; only a network error lands here.
      setStatus("error");
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 text-center shadow-[var(--shadow-window)]">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[var(--color-accent-amber-soft,#fdf0d5)] text-[var(--color-accent-amber-dark)]">
          <MailCheck size={24} />
        </div>

        <h1 className="mb-2 text-[22px] font-semibold text-[var(--color-text-strong)]">
          Check your inbox
        </h1>
        <p className="mb-6 text-[13px] leading-relaxed text-[var(--color-text-muted)]">
          We&apos;ve sent a verification link
          {email ? (
            <>
              {" "}
              to{" "}
              <span className="font-semibold text-[var(--color-text-body)]">
                {email}
              </span>
            </>
          ) : null}
          . Click it to activate your account, then sign in to set up your
          workspace.
        </p>

        {status === "sent" ? (
          <p className="mb-4 rounded-[var(--radius-md)] bg-[var(--color-success-soft,#e7f6ec)] px-3 py-2 text-[12px] text-[var(--color-text-success,#1a7f37)]">
            Verification email re-sent. Give it a minute, then check spam too.
          </p>
        ) : null}
        {status === "error" ? (
          <p className="mb-4 rounded-[var(--radius-md)] bg-[var(--color-danger-soft,#fce8e8)] px-3 py-2 text-[12px] text-[var(--color-text-danger)]">
            Couldn&apos;t resend right now. Please try again shortly.
          </p>
        ) : null}

        {email ? (
          <Button
            type="button"
            variant="secondary"
            size="md"
            fullWidth
            isLoading={status === "sending"}
            disabled={status === "sending"}
            onClick={() => void resend()}
          >
            {status === "sending" ? "Resending…" : "Resend verification email"}
          </Button>
        ) : null}

        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
          Already verified?{" "}
          <Link
            to="/login"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Sign in
          </Link>
        </div>
      </div>
    </main>
  );
}
